import { warn } from '../../../../utils/logging/warn.mjs';
import { CLEAR } from '../../gl/const.mjs';
import { CanvasSource } from '../../shared/texture/sources/CanvasSource.mjs';
import { TextureSource } from '../../shared/texture/sources/TextureSource.mjs';
import { GpuRenderTarget } from './GpuRenderTarget.mjs';

"use strict";
const canvasAllowedFormats = {
  bgra8unorm: true,
  rgba8unorm: true,
  rgba16float: true
};
function getCanvasContextFormat(format) {
  if (canvasAllowedFormats[format]) return format;
  const preferred = navigator.gpu.getPreferredCanvasFormat();
  warn(`[WebGPU] CanvasSource format '${format}' is not a valid GPUCanvasContext format. Falling back to '${preferred}'. Allowed formats are: bgra8unorm, rgba8unorm, rgba16float.`);
  return preferred;
}
class GpuRenderTargetAdaptor {
  constructor() {
    /**
     * The render target the currently open render pass is rendering to (plus the subresource it is
     * bound to). Used to make {@link startRenderPass} idempotent: binding the same target/mip/layer
     * again with no clear reuses the open pass instead of tearing it down and beginning a new one.
     * Reset to `null` whenever the pass is closed ({@link finishRenderPass}).
     */
    this._activePass = null;
  }
  init(renderer, renderTargetSystem) {
    this._renderer = renderer;
    this._renderTargetSystem = renderTargetSystem;
  }
  copyToTexture(sourceRenderSurfaceTexture, destinationTexture, originSrc, size, originDest) {
    const renderer = this._renderer;
    this.finishRenderPass();
    const baseGpuTexture = this._getGpuColorTexture(
      sourceRenderSurfaceTexture
    );
    const backGpuTexture = renderer.texture.getGpuSource(
      destinationTexture.source
    );
    renderer.encoder.commandEncoder.copyTextureToTexture(
      {
        texture: baseGpuTexture,
        origin: originSrc
      },
      {
        texture: backGpuTexture,
        origin: originDest
      },
      size
    );
    return destinationTexture;
  }
  copyDepthTexture(source, destination, originSrc, size, originDest) {
    const renderer = this._renderer;
    this.finishRenderPass();
    const srcDepth = source.depthStencilAttachment.texture;
    const srcGpu = renderer.texture.getGpuSource(srcDepth);
    const dstGpu = renderer.texture.getGpuSource(destination.source);
    const standAlone = renderer.encoder.commandEncoder === null;
    const commandEncoder = standAlone ? renderer.gpu.device.createCommandEncoder() : renderer.encoder.commandEncoder;
    commandEncoder.copyTextureToTexture(
      { texture: srcGpu, origin: originSrc },
      { texture: dstGpu, origin: originDest },
      { width: size.width, height: size.height }
    );
    if (standAlone) {
      renderer.gpu.device.queue.submit([commandEncoder.finish()]);
    }
  }
  startRenderPass(renderTarget, clear = true, clearColor, viewport, mipLevel = 0, layer = 0) {
    const renderTargetSystem = this._renderTargetSystem;
    const gpuRenderTarget = renderTargetSystem.getGpuRenderTarget(renderTarget);
    if (layer !== 0 && gpuRenderTarget.msaaTextures?.length) {
      throw new Error("[RenderTargetSystem] Rendering to array layers is not supported with MSAA render targets.");
    }
    if (mipLevel > 0 && gpuRenderTarget.msaaTextures?.length) {
      throw new Error("[RenderTargetSystem] Rendering to mip levels is not supported with MSAA render targets.");
    }
    let clearBits = clear;
    if (typeof clearBits === "boolean") {
      clearBits = clearBits ? CLEAR.ALL : CLEAR.NONE;
    }
    if ((renderTarget.stencil || renderTarget.depth) && !renderTarget.depthStencilAttachment) {
      renderTarget.ensureDepthStencilTexture();
    }
    const hasDepthStencil = !!renderTarget.depthStencilAttachment;
    const activePass = this._activePass;
    const reuse = activePass !== null && activePass.renderTarget === renderTarget && activePass.mipLevel === mipLevel && activePass.layer === layer && activePass.depthStencil === hasDepthStencil && this._renderer.encoder.renderPassEncoder !== null && clearBits === CLEAR.NONE;
    this._renderer.pipeline.setRenderTarget(renderTarget);
    if (reuse) {
      this._renderer.encoder.setViewport(viewport);
      return;
    }
    const descriptor = this.getDescriptor(renderTarget, clear, clearColor, mipLevel, layer);
    gpuRenderTarget.descriptor = descriptor;
    this._renderer.encoder.beginRenderPass(gpuRenderTarget);
    this._renderer.encoder.setViewport(viewport);
    this._activePass = { renderTarget, mipLevel, layer, depthStencil: hasDepthStencil };
  }
  finishRenderPass() {
    this._renderer.encoder.endRenderPass();
    this._activePass = null;
  }
  /**
   * returns the gpu texture for the first color texture in the render target
   * mainly used by the filter manager to get copy the texture for blending
   * @param renderTarget
   * @returns a gpu texture
   */
  _getGpuColorTexture(renderTarget) {
    if (renderTarget.colorAttachments.length === 0) {
      throw new Error("[GpuRenderTargetAdaptor] cannot get gpu color texture from a depth-only render target");
    }
    const colorTexture = renderTarget.colorAttachments[0].texture;
    if (colorTexture instanceof CanvasSource && colorTexture._gpuContext) {
      return colorTexture._gpuContext.getCurrentTexture();
    }
    return this._renderer.texture.getGpuSource(colorTexture);
  }
  getDescriptor(renderTarget, clear, clearValue, mipLevel = 0, layer = 0) {
    if (typeof clear === "boolean") {
      clear = clear ? CLEAR.ALL : CLEAR.NONE;
    }
    const renderTargetSystem = this._renderTargetSystem;
    const gpuRenderTarget = renderTargetSystem.getGpuRenderTarget(renderTarget);
    const colorAttachments = renderTarget.colorAttachments.map(
      (colorAttachment, i) => {
        const colorTexture = colorAttachment.texture;
        const context = colorTexture instanceof CanvasSource ? colorTexture._gpuContext : null;
        let view;
        let resolveTarget;
        if (context) {
          if (layer !== 0) {
            throw new Error("[RenderTargetSystem] Rendering to array layers is not supported for canvas targets.");
          }
          const currentTexture = context.getCurrentTexture();
          const canvasTextureView = currentTexture.createView(colorAttachment.viewDescriptor);
          view = canvasTextureView;
        } else {
          view = this._renderer.texture.getTextureRenderTargetView(
            colorAttachment.texture,
            mipLevel,
            layer,
            colorAttachment.viewDescriptor
          );
        }
        let attachmentIsTransient = false;
        if (gpuRenderTarget.msaaTextures[i]) {
          resolveTarget = view;
          view = this._renderer.texture.getTextureView(
            gpuRenderTarget.msaaTextures[i]
          );
          attachmentIsTransient = gpuRenderTarget.msaaTextures[i].transient;
        }
        let loadOp = colorAttachment.loadOp;
        if (clear !== void 0) {
          loadOp = clear & CLEAR.COLOR ? "clear" : "load";
        }
        clearValue ?? (clearValue = renderTargetSystem.defaultClearColor);
        const storeOp = colorAttachment.storeOp ?? "store";
        const baseAttachment = {
          view,
          resolveTarget,
          // Only discard the MSAA buffer when it was created as transient — i.e. we know
          // no later pass will try to load it. Non-transient MSAA targets keep storeOp:'store'
          // so flows like filter pop-back (loadOp:'load' on the parent RT) keep working.
          storeOp: attachmentIsTransient ? "discard" : storeOp,
          loadOp
        };
        if (loadOp === "clear") {
          clearValue ?? (clearValue = colorAttachment.clearValue ?? renderTargetSystem.defaultClearColor);
          baseAttachment.clearValue = clearValue;
        }
        for (const key in colorAttachment) {
          if (key !== "texture" && key !== "viewDescriptor" && key !== "clearValue" && key !== "loadOp" && key !== "storeOp") {
            baseAttachment[key] = colorAttachment[key];
          }
        }
        return baseAttachment;
      }
    );
    let depthStencilAttachment;
    if (renderTarget.depthStencilAttachment) {
      if (gpuRenderTarget.msaa) {
        renderTarget.depthStencilAttachment.texture.sampleCount = 4;
      }
      renderTarget.depthStencilAttachment.texture.transient = !!gpuRenderTarget.msaaTextures[0]?.transient;
      const attachment = renderTarget.depthStencilAttachment;
      const stencil = attachment.texture.format.includes("stencil");
      const depth = attachment.texture.format.includes("depth");
      const dsStoreOp = attachment.texture.transient ? "discard" : "store";
      depthStencilAttachment = {
        view: this._renderer.texture.getTextureRenderTargetView(
          attachment.texture,
          mipLevel,
          layer,
          attachment.viewDescriptor
        )
      };
      const depthReadOnly = attachment.depthReadOnly ?? false;
      const stencilReadOnly = attachment.stencilReadOnly ?? depthReadOnly;
      if (stencil && !stencilReadOnly) {
        depthStencilAttachment.stencilLoadOp = clear & CLEAR.STENCIL ? "clear" : attachment.stencilLoadOp ?? "load";
        depthStencilAttachment.stencilStoreOp = attachment.stencilStoreOp ?? dsStoreOp;
        if (depthStencilAttachment.stencilLoadOp === "clear") {
          depthStencilAttachment.stencilClearValue = attachment.stencilClearValue ?? 0;
        }
      } else if (stencil && stencilReadOnly) {
        depthStencilAttachment.stencilReadOnly = true;
      }
      if (depth && !depthReadOnly) {
        depthStencilAttachment.depthLoadOp = clear & CLEAR.DEPTH ? "clear" : attachment.depthLoadOp ?? "load";
        depthStencilAttachment.depthStoreOp = attachment.depthStoreOp ?? dsStoreOp;
        if (depthStencilAttachment.depthLoadOp === "clear") {
          depthStencilAttachment.depthClearValue = attachment.depthClearValue ?? 1;
        }
      } else if (depth && depthReadOnly) {
        depthStencilAttachment.depthReadOnly = true;
      }
      for (const key in attachment) {
        if (key !== "texture" && key !== "viewDescriptor" && key !== "stencilLoadOp" && key !== "stencilStoreOp" && key !== "stencilClearValue" && key !== "stencilReadOnly" && key !== "depthLoadOp" && key !== "depthStoreOp" && key !== "depthClearValue" && key !== "depthReadOnly") {
          depthStencilAttachment[key] = attachment[key];
        }
      }
    }
    const descriptor = {
      colorAttachments,
      depthStencilAttachment,
      label: renderTarget.label
    };
    return descriptor;
  }
  clear(renderTarget, clear = true, clearColor, viewport, mipLevel = 0, layer = 0) {
    if (!clear) return;
    const { gpu, encoder } = this._renderer;
    const device = gpu.device;
    const standAlone = encoder.commandEncoder === null;
    if (standAlone) {
      const commandEncoder = device.createCommandEncoder();
      const renderPassDescriptor = this.getDescriptor(renderTarget, clear, clearColor, mipLevel, layer);
      const passEncoder = commandEncoder.beginRenderPass(renderPassDescriptor);
      passEncoder.setViewport(viewport.x, viewport.y, viewport.width, viewport.height, 0, 1);
      passEncoder.end();
      const gpuCommands = commandEncoder.finish();
      device.queue.submit([gpuCommands]);
    } else {
      this.startRenderPass(renderTarget, clear, clearColor, viewport, mipLevel, layer);
    }
  }
  initGpuRenderTarget(renderTarget) {
    renderTarget.isRoot = true;
    const gpuRenderTarget = new GpuRenderTarget();
    renderTarget.colorAttachments.forEach((colorAttachment, i) => {
      const colorTexture = colorAttachment.texture;
      if (colorTexture instanceof CanvasSource) {
        const context = colorTexture._gpuContext ?? (colorTexture._gpuContext = colorTexture.resource.getContext(
          "webgpu"
        ));
        const alphaMode = colorTexture.transparent ? "premultiplied" : "opaque";
        const canvasFormat = getCanvasContextFormat(colorTexture.format);
        try {
          context.configure({
            device: this._renderer.gpu.device,
            usage: GPUTextureUsage.TEXTURE_BINDING | GPUTextureUsage.COPY_DST | GPUTextureUsage.RENDER_ATTACHMENT | GPUTextureUsage.COPY_SRC,
            format: canvasFormat,
            alphaMode,
            ...canvasFormat === "rgba16float" ? { toneMapping: { mode: "extended" } } : {}
          });
        } catch (e) {
          console.error(e);
        }
        gpuRenderTarget.contexts[i] = context;
      }
      gpuRenderTarget.msaa = colorTexture.source.antialias;
      if (colorTexture.antialias) {
        const msaaTexture = new TextureSource({
          width: 0,
          height: 0,
          sampleCount: 4,
          // WebGPU requires multisampled textures to have exactly 1 mip level, so this must never
          // inherit TextureSource.defaultOptions.autoGenerateMipmaps
          autoGenerateMipmaps: false,
          transient: colorTexture.transient,
          arrayLayerCount: colorTexture.arrayLayerCount,
          format: colorTexture.format
        });
        gpuRenderTarget.msaaTextures[i] = msaaTexture;
      }
    });
    if (gpuRenderTarget.msaa) {
      gpuRenderTarget.msaaSamples = 4;
      if (renderTarget.depthStencilAttachment) {
        renderTarget.depthStencilAttachment.texture.sampleCount = 4;
        renderTarget.depthStencilAttachment.texture.transient = !!gpuRenderTarget.msaaTextures[0]?.transient;
      }
    }
    return gpuRenderTarget;
  }
  destroyGpuRenderTarget(gpuRenderTarget) {
    gpuRenderTarget.contexts.forEach((context) => {
      context.unconfigure();
    });
    gpuRenderTarget.msaaTextures.forEach((texture) => {
      texture.destroy();
    });
    gpuRenderTarget.msaaTextures.length = 0;
    gpuRenderTarget.contexts.length = 0;
  }
  ensureDepthStencilTexture(renderTarget) {
    const gpuRenderTarget = this._renderTargetSystem.getGpuRenderTarget(renderTarget);
    if (renderTarget.depthStencilAttachment && gpuRenderTarget.msaa) {
      renderTarget.depthStencilAttachment.texture.sampleCount = 4;
    }
  }
  resizeGpuRenderTarget(renderTarget) {
    const gpuRenderTarget = this._renderTargetSystem.getGpuRenderTarget(renderTarget);
    gpuRenderTarget.width = renderTarget.width;
    gpuRenderTarget.height = renderTarget.height;
    if (gpuRenderTarget.msaa) {
      renderTarget.colorAttachments.forEach((colorAttachment, i) => {
        const colorTexture = colorAttachment.texture;
        const msaaTexture = gpuRenderTarget.msaaTextures[i];
        msaaTexture?.resize(
          colorTexture.width,
          colorTexture.height,
          colorTexture._resolution
        );
      });
    }
  }
}

export { GpuRenderTargetAdaptor };
//# sourceMappingURL=GpuRenderTargetAdaptor.mjs.map
